/**
 * Style Linter del Dossier · valida la calidad de una sección antes de presentar.
 *
 * Detecta:
 *  - Texto que va a generar muchos slides paginados (warning amarillo)
 *  - Sección vacía (warning gris)
 *  - Plantilla manual con baja capacidad para texto largo (warning rojo)
 *  - Imagen ausente cuando el layout la requiere (warning rojo)
 *  - Título de sección demasiado largo (warning amarillo)
 *  - Texto que se va a truncar incluso paginando (warning rojo)
 *
 * Pensado para mostrarse bajo el header de cada sección en `DossierTab.jsx`
 * como un badge 🟢🟡🔴 que da feedback al usuario antes de presentar.
 */
import {
  layoutCapacity,
  paginateText,
} from "@/components/dossier/dossier_pagination";
import { resolveAuto } from "@/components/dossier/DossierSlide";

const NEEDS_IMAGE_LAYOUTS = new Set([
  "cover",
  "fullbleed",
  "grid-2x2",
  "half-split",
  "text-image-right",
  "text-image-left",
  "portrait-quote",
]);

/**
 * Calcula la "salud" de una sección de cara a la presentación.
 * Devuelve `{ level, slides, warnings }` donde:
 *   - level: "ok" | "warn" | "error"
 *   - slides: número de slides que generará
 *   - warnings: lista de { code, message, level }
 */
export function sectionQuality(section) {
  const warnings = [];
  const text = section?.text || "";
  const imgs = section?.images || [];
  const items = section?.items || [];
  const userLayout = section?.layout || "";

  // ============================================================
  // Caso 1: Sección vacía
  // ============================================================
  if (!text.trim() && imgs.length === 0 && items.length === 0) {
    return {
      level: "empty",
      slides: 0,
      warnings: [{
        code: "empty",
        level: "empty",
        message: "Sin contenido. No genera ningún slide.",
      }],
    };
  }

  // ============================================================
  // Determinar layout efectivo y N de slides
  // ============================================================
  const autoLayout = resolveAuto(section);
  const effectiveLayout = userLayout || autoLayout;
  const cap = layoutCapacity(effectiveLayout);

  // Capacidad efectiva para paginación (puede promocionarse si user-set es pequeño)
  let pagingCap = cap;
  let promoted = false;
  if (text.length > 700 && cap > 0 && cap < 700) {
    pagingCap = imgs.length > 0 ? 750 : 1500;
    promoted = userLayout && cap > 0;
  }

  let slides = 1;
  if (pagingCap && text.length > pagingCap) {
    const parts = paginateText(text, pagingCap);
    slides = parts.length;
  }

  // ============================================================
  // Warnings específicos
  // ============================================================

  // a) Si el usuario eligió un layout con capacidad baja y el sistema lo
  //    promocionó para no truncar
  if (promoted) {
    warnings.push({
      code: "layout_promoted",
      level: "warn",
      message: `Tu plantilla manual sólo aguanta ${cap} caracteres pero el texto tiene ${text.length}. El sistema usará un layout con más capacidad para paginar correctamente. Considera elegir un layout adecuado (p. ej. "Texto + imagen" o "Dos columnas").`,
    });
  }

  // b) Si el layout requiere imagen pero no hay
  if (NEEDS_IMAGE_LAYOUTS.has(effectiveLayout) && imgs.length === 0
      && effectiveLayout !== "cover") {
    warnings.push({
      code: "needs_image",
      level: "error",
      message: `La plantilla "${effectiveLayout}" muestra mejor con una imagen y no hay ninguna añadida. Sube una foto o cambia el layout.`,
    });
  }

  // c) Generará muchos slides paginados
  if (slides >= 4) {
    warnings.push({
      code: "many_slides",
      level: "warn",
      message: `Se generarán ${slides} slides paginados a partir de este texto. Si quieres menos, considera resumir o partir manualmente la sección.`,
    });
  }

  // d) Título demasiado largo
  const label = section?.label || "";
  if (label.length > 35) {
    warnings.push({
      code: "label_too_long",
      level: "warn",
      message: `El título de la sección ("${label}") tiene ${label.length} caracteres. Puede no caber en 1 línea en el slide. Considera acortarlo.`,
    });
  }

  // e) Quote layout pero el texto no tiene cita/comillas
  if (effectiveLayout === "quote" || effectiveLayout === "portrait-quote") {
    const hasQuote = /[«"\u201c\u201d]/.test(text);
    if (text.length > 250 || !hasQuote) {
      warnings.push({
        code: "weak_quote",
        level: "warn",
        message: `La plantilla "Cita" funciona mejor con un texto corto entrecomillado (≤200c). El texto actual tiene ${text.length}c. Sólo se mostrará la 1ª frase.`,
      });
    }
  }

  // f) Imagen vacía aunque haya texto suficiente y el layout sería mejor con foto
  if (imgs.length === 0 && text.length > 600 && !userLayout) {
    // Solo sugerencia leve
    warnings.push({
      code: "no_image_suggestion",
      level: "info",
      message: "Sin imagen. Si añades una foto evocadora, el slide quedará más cinematográfico.",
    });
  }

  // ============================================================
  // Nivel global = el peor de los warnings (si los hay)
  // ============================================================
  let level = "ok";
  if (warnings.some((w) => w.level === "error")) level = "error";
  else if (warnings.some((w) => w.level === "warn")) level = "warn";
  else if (warnings.some((w) => w.level === "info")) level = "info";

  return { level, slides, warnings, effectiveLayout };
}

/** Color asociado a un nivel. */
export const LEVEL_COLORS = {
  ok:    { bg: "bg-emerald-50",  border: "border-emerald-300",  text: "text-emerald-800",  dot: "#10b981", label: "Genera 1 slide perfecto" },
  info:  { bg: "bg-sky-50",      border: "border-sky-300",       text: "text-sky-800",      dot: "#0ea5e9", label: "Mejorable" },
  warn:  { bg: "bg-amber-50",    border: "border-amber-300",     text: "text-amber-900",    dot: "#f59e0b", label: "Revisar" },
  error: { bg: "bg-rose-50",     border: "border-rose-300",      text: "text-rose-800",     dot: "#ef4444", label: "Necesita atención" },
  empty: { bg: "bg-stone-50",    border: "border-stone-300",     text: "text-stone-600",    dot: "#a8a29e", label: "Sin contenido" },
};
